import {
  Grid,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { Colors, isLightTheme } from '../config/default';
import {
  supportingDocumentsPopup,
  commitmentPosition as commitmentLabels
} from '../constants/appConstants';
import CommitmentPositionPanel from './commitmentPositionPanel';

/**
 * Four amount columns in two labelled PAIRS, and the pairing is the whole point.
 *
 * `receivedAmt`/`remainingAmt` is the ERP's own position — Vista and Spectrum
 * report it per line and the sync writes it straight through, and every
 * comparison in the system already reads it. `bhReceivedAmt`/`bhRemainingAmt`
 * is what documents approved in THIS app have drawn against the line.
 *
 * They legitimately differ, and the difference is what the customer is trying to
 * see: their older invoices live in the ERP and were never processed here, while
 * an invoice approved here and not yet exported has moved BH's side alone.
 * Adding the two double-counts everything that completed the round trip, so they
 * are never merged — they are grouped under two headings instead, so a reader
 * can tell at a glance which side a number came from.
 *
 * BH's pair is empty until this app consumes the line. Foundation reports
 * nothing per line either way, so its ERP answer is commitment-level only and
 * shows in the panel above this table rather than in these columns.
 *
 * THE CELL NAMES ARE THE BACKEND'S STORED FIELD NAMES, verbatim. The headings
 * below are this component's business and exist nowhere else.
 */
const CELL_KEYS = {
  description: 'Description',
  amount: 'Amount',
  erpReceived: 'receivedAmt',
  erpRemaining: 'remainingAmt',
  bhReceived: 'bhReceivedAmt',
  bhRemaining: 'bhRemainingAmt'
};

// Older payloads spelled the two ERP cells out in prose. Kept as a fallback so
// either deploy order works and a response already in flight still renders.
const LEGACY_CELL_KEYS = {
  [CELL_KEYS.erpReceived]: 'Received Amount',
  [CELL_KEYS.erpRemaining]: 'Remaining Amount',
  [CELL_KEYS.bhReceived]: 'BH Received Amount',
  [CELL_KEYS.bhRemaining]: 'BH Remaining Amount'
};

const rawCell = (lineItem, key) => {
  const byKey = lineItem?.find((cell) => cell?.name === key);
  const legacy = LEGACY_CELL_KEYS[key];
  const cell = byKey || (legacy ? lineItem?.find((c) => c?.name === legacy) : undefined);
  const value = cell?.value;
  return value === null || value === undefined || String(value).trim() === ''
    ? null
    : value;
};

export const lineItemCell = (lineItem, key) => rawCell(lineItem, key) ?? '-';

/**
 * A change order the backend appended to the commitment's lines. It carries an
 * amount and a description only — nobody reports what was billed against one
 * change order — so its consumption cells are blank and render as dashes. It is
 * part of what the commitment is worth, never a line to match or copy.
 */
export const isChangeOrderLine = (lineItem) =>
  String(rawCell(lineItem, 'isChangeOrder') ?? '').toLowerCase() === 'true';

/**
 * BH's position OPENS FROM THE ERP'S, which is not a display convenience — it
 * is the same rule the backend's own calculation applies
 * (`bhReceivedAmt ?? receivedAmt`, erpPurchaseOrder.ts). A line this app has
 * never drawn against has consumed nothing, so what is left on it is exactly
 * what the ERP says is left.
 *
 * Rendering a dash there said "no data" about a line whose answer is known, and
 * beside a populated ERP column it read as zero remaining. The two must agree,
 * because they describe the same moment: the instant BH first consumes the
 * line, the calculation starts from this very number, so the figure on screen
 * would otherwise jump from blank to a value it had implied all along.
 *
 * The fallback is only ever taken while BH's own figure is absent. Once the
 * calculation has written one it always wins, including when it is a real `0`.
 */
export const resolveLineAmounts = (lineItem) => {
  const erpReceived = rawCell(lineItem, CELL_KEYS.erpReceived);
  const erpRemaining = rawCell(lineItem, CELL_KEYS.erpRemaining);
  return {
    erpReceived: erpReceived ?? '-',
    erpRemaining: erpRemaining ?? '-',
    bhReceived: rawCell(lineItem, CELL_KEYS.bhReceived) ?? erpReceived ?? '-',
    bhRemaining: rawCell(lineItem, CELL_KEYS.bhRemaining) ?? erpRemaining ?? '-'
  };
};

function PoLineitems({ supportindDocLineitems, commitmentPosition }) {
  const {
    DESCRIPTION,
    PO_LINEITEMS,
    AMOUNT,
    RECIEVED,
    REMAINING,
    ERP_COLUMN_GROUP,
    BH_COLUMN_GROUP
  } = supportingDocumentsPopup;
  const rowText = isLightTheme() ? Colors.TEXTGREY : '#ccc';
  // `tableLayout: fixed` gives each cell a width it cannot grow past, so
  // anything that refuses to wrap spills over its neighbour instead — which is
  // how six headings ended up printed on top of each other. Headings WRAP;
  // values, which are short, are clipped rather than allowed to overlap.
  const rowStyle = {
    color: rowText,
    textAlign: 'right',
    fontSize: '10px',
    padding: '6px 4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
  const headStyle = {
    fontWeight: 400,
    color: Colors.WHITE,
    fontSize: '.7rem',
    lineHeight: 1.15,
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  };
  // TWO GROUPS, AND THEY MUST LOOK LIKE TWO. The underline belongs to the label
  // rather than to the table cell: a cell-level border butts straight up against
  // its neighbour's and the two groups read as one continuous rule spanning all
  // four columns — which is the opposite of what the grouping is for. Drawn on
  // the label, the cell's own horizontal padding becomes a real gap between the
  // two lines.
  const groupCell = {
    padding: '4px 6px',
    borderBottom: 'none'
  };
  const groupLabel = {
    ...headStyle,
    textAlign: 'center',
    paddingBottom: '3px',
    borderBottom: `1px solid ${Colors.TEXTGREY}`
  };
  // ...and a divider down the seam, so the split survives into the rows where
  // the numbers actually are. Without it the underlines separate the headings
  // and nothing separates the values they describe.
  const groupDivider = {
    borderLeft: `1px solid ${isLightTheme() ? 'rgba(0,0,0,.12)' : 'rgba(255,255,255,.14)'}`
  };

  return (
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          p: 2,
          backgroundColor: Colors.NAV_BLACK,
          borderRadius: 2,
          height: '65vh',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0
        }}>
        <Typography sx={{ color: Colors.TOURQUISE, mb: '.5rem' }}>{PO_LINEITEMS}</Typography>
        {/* The commitment's position, which for Foundation is the only place an
            ERP figure can appear — it reports nothing per line. */}
        <CommitmentPositionPanel position={commitmentPosition} />
        <Box
          sx={{
            p: 1.5,
            mt: '.6rem',
            backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
            borderRadius: 2,
            // Flex rather than a fixed vh, so the panel above takes the height it
            // needs and this table gets whatever is left instead of overflowing
            // the card.
            flex: 1,
            minHeight: 0,
            overflow: 'auto'
          }}>
          {/* minWidth + the horizontal scroll on the box above: six columns in a
              third of the modal do not fit on a narrow screen, and letting them
              squeeze below this width is what made the headings unreadable. */}
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: '420px' }}>
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <TableHead>
              {/* Two header rows: which SIDE the number came from, then which
                  number it is. A single row of four amount columns cannot say
                  that, and "BH Received" beside "Received" was being read as one
                  running-on heading. */}
              <TableRow>
                <TableCell sx={{ borderBottom: 'none', padding: '4px' }} />
                <TableCell sx={{ borderBottom: 'none', padding: '4px' }} />
                <TableCell colSpan={2} sx={groupCell}>
                  <Typography sx={groupLabel}>{ERP_COLUMN_GROUP}</Typography>
                </TableCell>
                <TableCell colSpan={2} sx={{ ...groupCell, ...groupDivider }}>
                  <Typography sx={groupLabel}>{BH_COLUMN_GROUP}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ padding: '6px 4px' }}>
                  <Typography sx={headStyle}>{DESCRIPTION}</Typography>
                </TableCell>
                <TableCell sx={{ padding: '6px 4px', textAlign: 'right' }}>
                  <Typography sx={headStyle}>{AMOUNT}</Typography>
                </TableCell>
                {[RECIEVED, REMAINING, RECIEVED, REMAINING].map((label, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      padding: '6px 4px',
                      textAlign: 'right',
                      // idx 2 is the first BH column — the seam between the two
                      // groups.
                      ...(idx === 2 ? groupDivider : {})
                    }}>
                    <Typography sx={headStyle}>{label}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {supportindDocLineitems?.map((item, idx) => {
                const amounts = resolveLineAmounts(item);
                return (
                  <TableRow key={idx} hover>
                    <TableCell
                      sx={{
                        color: rowText,
                        fontSize: '10px',
                        padding: '6px 4px',
                        wordBreak: 'break-word'
                      }}>
                      {lineItemCell(item, CELL_KEYS.description)}
                      {isChangeOrderLine(item) ? (
                        <Typography
                          component="span"
                          sx={{
                            display: 'block',
                            color: Colors.TOURQUISE,
                            fontSize: '9px',
                            fontStyle: 'italic'
                          }}>
                          {commitmentLabels.CHANGE_ORDER_TAG}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={rowStyle}>{lineItemCell(item, CELL_KEYS.amount)}</TableCell>
                    <TableCell sx={rowStyle}>{amounts.erpReceived}</TableCell>
                    <TableCell sx={rowStyle}>{amounts.erpRemaining}</TableCell>
                    <TableCell sx={{ ...rowStyle, ...groupDivider }}>
                      {amounts.bhReceived}
                    </TableCell>
                    <TableCell sx={rowStyle}>{amounts.bhRemaining}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Grid>
  );
}

export default PoLineitems;
